"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Fires once when the returned ref first enters the viewport.
 *
 * This exists because of masked type. Framer's `whileInView` measures the element
 * it is attached to, and a line sitting inside an `overflow: hidden` mask, still
 * translated fully below its own clipped edge, reports no intersection area. The
 * reveal then never fires and the headline stays invisible rather than merely
 * un-animated. So the observer goes on the mask's parent, which is never clipped,
 * and the masked child animates off the boolean this returns.
 *
 * A headline that never appears is a broken page, so this errs hard towards firing:
 * no negative root margin, a zero threshold, and scroll and resize listeners as a
 * backstop for the cases an observer alone can miss (a section that lands mid
 * viewport straight after a pinned block, a resize that moves the element while no
 * scroll event fires). Without IntersectionObserver at all, it reveals immediately.
 */
export const useInViewOnce = <T extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<T>(null);
  // Always false on the first render. Server and pre-hydration client must agree,
  // and the server has no IntersectionObserver to test, so a browser lacking one
  // is handled in the effect below rather than by a different initial value here.
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // No observer in this browser: nothing will ever fire, so reveal rather than
    // leave masked type clipped forever. The set-state-in-effect rule wants this
    // decided during render, but the server cannot test for a browser API without
    // disagreeing with the client on the first paint. One extra render on a browser
    // this old is the cheaper trade than a hydration mismatch on every modern one.
    if (typeof IntersectionObserver === "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSeen(true);
      return;
    }

    let done = false;

    const check = () => {
      const box = node.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) reveal();
    };

    const reveal = () => {
      if (done) return;
      done = true;
      setSeen(true);
      observer.disconnect();
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };

    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && reveal(), {
      rootMargin: "0px",
      threshold: 0,
    });

    observer.observe(node);
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    check();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  return [ref, seen] as const;
};
